import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import digitalseoImg from "@/assets/portfolio/digitalseo.jpg";
import demandsaImg from "@/assets/portfolio/demandsa.jpg";
import onelineImg from "@/assets/portfolio/onelinemaintenance.jpg";
import gmbImg from "@/assets/portfolio/gmb.jpg";
import leadgenImg from "@/assets/portfolio/leadgen.jpg";
import constructionImg from "@/assets/portfolio/onelineconstruction.jpg";

type Item = {
  tag: string;
  title: string;
  client: string;
  url?: string;
  image: string;
};

const items: Item[] = [
  {
    tag: "Web Development",
    title: "DigitalSEO",
    client: "digtalseo.net",
    url: "https://digtalseo.net",
    image: digitalseoImg,
  },
  {
    tag: "App Development",
    title: "DemandSA",
    client: "demandsa.com",
    url: "https://demandsa.com",
    image: demandsaImg,
  },
  {
    tag: "Digital Marketing",
    title: "One Line Maintenance",
    client: "onelinemaintenance.com",
    url: "https://onelinemaintenance.com",
    image: onelineImg,
  },
  {
    tag: "Local SEO",
    title: "Google My Business",
    client: "GMB listing & optimization",
    image: gmbImg,
  },
  {
    tag: "Lead Generation",
    title: "High-Intent Lead Funnels",
    client: "Multi-industry campaigns",
    image: leadgenImg,
  },
  {
    tag: "UI / UX Design",
    title: "One Line Construction",
    client: "Web & mobile design system",
    image: constructionImg,
  },
];

export function Portfolio() {
  return (
    <section className="py-24">
      <div className="mx-auto max-w-7xl px-4 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-12">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-primary">
              Selected work
            </div>
            <h2 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">
              Recent <span className="text-gradient">projects</span>
            </h2>
          </div>
          <p className="max-w-md text-muted-foreground">
            Real clients, real outcomes — across web, mobile, marketing and SEO.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {items.map((it, i) => {
            const Wrapper: React.ElementType = it.url ? "a" : "div";
            const wrapperProps = it.url
              ? { href: it.url, target: "_blank", rel: "noopener noreferrer" }
              : {};

            return (
              <motion.div
                key={it.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <Wrapper
                  {...wrapperProps}
                  className="group relative block overflow-hidden rounded-2xl border border-border bg-card aspect-[4/3] cursor-pointer shadow-card hover:border-primary/40 hover:shadow-xl transition-all duration-500"
                >
                  <img
                    src={it.image}
                    alt={`${it.title} — ${it.tag}`}
                    loading="lazy"
                    decoding="async"
                    width={600}
                    height={450}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

                  <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                    <span className="inline-flex items-center rounded-full bg-white/15 backdrop-blur-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white ring-1 ring-white/20">
                      {it.tag}
                    </span>
                    {it.url && (
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 backdrop-blur-md text-white ring-1 ring-white/20 opacity-0 group-hover:opacity-100 -translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    )}
                  </div>

                  <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                    <div className="text-lg font-semibold leading-tight">
                      {it.title}
                    </div>
                    <div className="mt-1 text-xs text-white/80">{it.client}</div>
                    {it.url && (
                      <div className="mt-3 text-xs font-semibold text-primary-foreground/90 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                        Visit site →
                      </div>
                    )}
                  </div>
                </Wrapper>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
